// Dashboard V2 Theme and Styling Constants
// Based on AIMHI brand colors (orange and gray)
export const dashboardTheme = {
  // AIMHI brand-aligned color palette
  colors: {
    // Primary orange from AIMHI logo
    primary: {
      main: '#FF8C00',  // AIMHI orange
      light: '#FFA726',
      dark: '#F57C00',
      gradient: 'linear-gradient(135deg, #FFB347 0%, #FF8C00 100%)'
    },
    // Soft blue-gray for balance
    secondary: {
      main: '#607D8B',
      light: '#90A4AE',
      dark: '#455A64',
      gradient: 'linear-gradient(135deg, #B0BEC5 0%, #607D8B 100%)'
    },
    // Calming teal for success
    success: {
      main: '#26A69A',
      light: '#4DB6AC',
      dark: '#00897B',
      gradient: 'linear-gradient(135deg, #80CBC4 0%, #26A69A 100%)'
    },
    // Soft blue for info
    info: {
      main: '#42A5F5',
      light: '#64B5F6',
      dark: '#1E88E5',
      gradient: 'linear-gradient(135deg, #90CAF9 0%, #42A5F5 100%)'
    },
    // Warm amber for warnings
    warning: {
      main: '#FFA726',
      light: '#FFB74D',
      dark: '#FB8C00',
      gradient: 'linear-gradient(135deg, #FFCC80 0%, #FFA726 100%)'
    },
    background: {
      gradient: 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)',
      card: '#ffffff',
      hover: 'rgba(255, 140, 0, 0.04)'
    },
    mood: {
      verySad: '#9E9E9E',
      sad: '#90CAF9',
      neutral: '#FFF176',
      good: '#AED581',
      great: '#66BB6A'
    }
  },
  
  // Enhanced shadows for depth
  shadows: {
    small: '0 2px 8px rgba(0, 0, 0, 0.06)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.08)',
    large: '0 8px 32px rgba(0, 0, 0, 0.12)',
    colored: (color) => `0 8px 32px ${color}20`,
    glow: (color) => `0 0 40px ${color}30`
  },
  
  // Animation constants
  animations: {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)'
  },
  
  // Border radius for modern look
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '16px',
    xl: '24px',
    full: '50%'
  }
};

// Widget styling presets
export const widgetStyles = {
  card: {
    borderRadius: '16px',
    background: '#ffffff',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)'
    }
  },
  
  gradientCard: (gradient) => ({
    background: gradient,
    color: '#ffffff',
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
      pointerEvents: 'none'
    }
  }),
  
  iconButton: {
    transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    '&:hover': {
      transform: 'scale(1.1) rotate(5deg)'
    },
    '&:active': {
      transform: 'scale(0.95)'
    }
  },
  
  progressBar: {
    height: 10,
    borderRadius: 5,
    background: 'rgba(0, 0, 0, 0.05)',
    '& .MuiLinearProgress-bar': {
      borderRadius: 5,
      background: 'linear-gradient(90deg, var(--color-start) 0%, var(--color-end) 100%)'
    }
  }
};

// Mood emoji configurations with animations
export const moodEmojis = [
  { 
    value: 1, 
    emoji: '😢', 
    label: 'Very Sad',
    color: '#9CA3AF',
    size: '2.5rem'
  },
  { 
    value: 3, 
    emoji: '😔', 
    label: 'Sad',
    color: '#93C5FD',
    size: '2.5rem'
  },
  { 
    value: 5, 
    emoji: '😐', 
    label: 'Neutral',
    color: '#FCD34D',
    size: '2.5rem'
  },
  { 
    value: 7, 
    emoji: '🙂', 
    label: 'Good',
    color: '#86EFAC',
    size: '2.5rem'
  },
  { 
    value: 10, 
    emoji: '😊', 
    label: 'Great',
    color: '#34D399',
    size: '2.5rem'
  }
];

// Quick action configurations
export const quickActions = [
  {
    id: 'chat',
    label: 'Chat',
    icon: '💬',
    color: '#5B8DEE',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'WITH YOUR THERAPIST'
  },
  {
    id: 'journal',
    label: 'Journal',
    icon: '📝',
    color: '#FF6B9D',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    description: 'WRITE & REFLECT'
  },
  {
    id: 'goals',
    label: 'Goals',
    icon: '🎯',
    color: '#48C78E',
    gradient: 'linear-gradient(135deg, #96e6a1 0%, #4fc978 100%)',
    description: 'TRACK PROGRESS'
  },
  {
    id: 'progress',
    label: 'Progress',
    icon: '📈',
    color: '#3ABFF8',
    gradient: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    description: 'VIEW INSIGHTS'
  },
  {
    id: 'session-summary',
    label: 'Session',
    icon: '📄',
    color: '#9B59B6',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    description: 'LATEST SESSION'
  },
  {
    id: 'check-in-history',
    label: 'History',
    icon: '📊',
    color: '#E67E22',
    gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    description: 'MOOD TRACKING'
  }
];